import type { FieldValueResult } from '@/components/documents/Foxit/FormMapping/composables/FieldValueProvider'
import { useFormFillHelper } from '@/components/documents/Foxit/FormMapping/composables/FormFillHelper'

export type SectionTotalConfig = {
	section: string
	emit?: boolean
}

export type GroupedTotalConfig = {
	key: string
	sections: string[]
}

export const defaultGroupedTotalSections: Record<string, string[]> = {
	guaranteed: ['service', 'merchandise', 'otherServices'],
	funeral: ['service', 'merchandise', 'cashAdvance', 'other', 'otherServices'],
	funeralWithTax: [
		'service',
		'merchandise',
		'cashAdvance',
		'cashAdvanceTax',
		'other',
		'otherServices',
	],
	cashAdvanceWithTax: ['cashAdvance', 'cashAdvanceTax'],
}

export const groupedTotal = (
	key: string,
	additionalSections: string[] = []
): GroupedTotalConfig => ({
	key,
	sections: [
		...(defaultGroupedTotalSections[key] ?? []),
		...additionalSections,
	],
})

export const useTotalFields = () => {
	const { fieldNameProvider } = useFormFillHelper()

	const getSectionTotal = (
		section: string,
		values: FieldValueResult[]
	): FieldValueResult => {
		return {
			name: fieldNameProvider.totals[section],
			value: values
				.filter((field) => field.section === section)
				.reduce(
					(acc, field) =>
						acc +
						(typeof field.value === 'string' ? 0 : (field.value as number)),
					0
				),
			format: true,
			section,
		}
	}

	const getGroupedTotal = (
		groupedTotalConfig: GroupedTotalConfig,
		sectionTotals: FieldValueResult[]
	): FieldValueResult => {
		const sum = sectionTotals.reduce((acc, sectionTotal) => {
			if (groupedTotalConfig.sections.includes(sectionTotal.section)) {
				return acc + (sectionTotal.value as number)
			}

			return acc
		}, 0)

		return {
			name: fieldNameProvider.totals[groupedTotalConfig.key],
			value: sum,
			format: true,
			section: 'totals',
		}
	}

	const getTotalsFields = (
		values: FieldValueResult[],
		sectionTotalConfigs: SectionTotalConfig[],
		groupedTotalConfigs: GroupedTotalConfig[]
	) => {
		const sectionTotals = sectionTotalConfigs.map((config) =>
			getSectionTotal(config.section, values)
		)
		const groupedTotals = groupedTotalConfigs.map((groupedTotalConfig) =>
			getGroupedTotal(groupedTotalConfig, sectionTotals)
		)
		const emittedSectionTotals = sectionTotalConfigs.flatMap((config, index) =>
			config.emit === false ? [] : [sectionTotals[index]]
		)

		return [...emittedSectionTotals, ...groupedTotals]
	}

	return {
		getSectionTotal,
		getGroupedTotal,
		getTotalsFields,
	}
}
